import { Component, ChangeDetectionStrategy, output, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-register',
  standalone: true,
  template: `
<div class="relative flex flex-grow items-center justify-center overflow-hidden bg-slate-950 px-4 py-14">
  <div class="absolute inset-0">
    <div class="absolute left-1/2 top-0 h-[380px] w-[380px] -translate-x-1/2 rounded-full bg-blue-600/20 blur-3xl"></div>
    <div class="absolute right-0 bottom-0 h-[320px] w-[320px] rounded-full bg-indigo-600/15 blur-2xl"></div>
  </div>

  <div class="relative w-full max-w-md rounded-3xl border border-slate-700/70 bg-slate-900/80 p-8 shadow-2xl backdrop-blur">
    @if (!registrationComplete()) {
      <div class="text-center">
        <div class="mx-auto mb-4 flex w-fit items-center gap-3 rounded-2xl border border-slate-700/80 bg-slate-950/80 px-3 py-2">
          <span class="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-indigo-900/30">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none">
              <path d="M16.5 7.5A6.5 6.5 0 1 0 16.5 16.5" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
              <path d="M10 14.5L14.5 10" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </span>
          <span class="text-lg font-black tracking-tight"><span class="text-white">Correct</span><span class="text-blue-400">ify</span></span>
        </div>
        <p class="text-xs uppercase tracking-widest text-blue-300">Cadastro</p>
        <h1 class="mt-3 text-3xl font-black text-white">Crie sua conta</h1>
        <p class="mt-2 text-sm text-slate-400">Ganhe crédito inicial e comece sua evolução hoje.</p>
      </div>

      <form class="mt-6 space-y-5" (ngSubmit)="onRegister()" #registerForm="ngForm">
        <div>
          <label for="name" class="mb-1 block text-sm font-semibold text-slate-300">Nome</label>
          <input
            id="name"
            name="name"
            type="text"
            autocomplete="name"
            required
            [(ngModel)]="name"
            #nameModel="ngModel"
            class="block w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder-slate-500 outline-none transition focus:border-blue-500"
            placeholder="Seu nome completo">
          @if (nameModel.invalid && (nameModel.dirty || nameModel.touched)) {
            <p class="mt-1 text-xs text-rose-400">Informe seu nome.</p>
          }
        </div>

        <div>
          <label for="email-address" class="mb-1 block text-sm font-semibold text-slate-300">E-mail</label>
          <input
            id="email-address"
            name="email"
            type="email"
            autocomplete="email"
            required
            email
            [(ngModel)]="email"
            #emailModel="ngModel"
            class="block w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder-slate-500 outline-none transition focus:border-blue-500"
            placeholder="seu@email.com">
          @if (emailModel.invalid && (emailModel.dirty || emailModel.touched)) {
            <p class="mt-1 text-xs text-rose-400">Informe um e-mail válido.</p>
          }
        </div>

        <div>
          <label for="password" class="mb-1 block text-sm font-semibold text-slate-300">Senha</label>
          <input
            id="password"
            name="password"
            type="password"
            autocomplete="new-password"
            required
            minlength="6"
            [(ngModel)]="password"
            #passwordModel="ngModel"
            class="block w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder-slate-500 outline-none transition focus:border-blue-500"
            placeholder="Mínimo de 6 caracteres">
          @if (passwordModel.invalid && (passwordModel.dirty || passwordModel.touched)) {
            <p class="mt-1 text-xs text-rose-400">A senha deve ter no mínimo 6 caracteres.</p>
          }
          @if (password.length >= 6) {
            <div class="mt-2">
              <div class="flex items-center gap-2">
                <div class="h-1.5 flex-1 rounded-full bg-slate-700">
                  <div class="h-1.5 rounded-full transition-all duration-300"
                    [style.width.%]="passwordStrength().percent"
                    [class.bg-red-500]="passwordStrength().level === 'fraca'"
                    [class.bg-yellow-500]="passwordStrength().level === 'média'"
                    [class.bg-emerald-500]="passwordStrength().level === 'forte'"></div>
                </div>
                <span class="text-xs font-medium"
                  [class.text-red-400]="passwordStrength().level === 'fraca'"
                  [class.text-yellow-400]="passwordStrength().level === 'média'"
                  [class.text-emerald-400]="passwordStrength().level === 'forte'">
                  Senha {{ passwordStrength().level }}
                </span>
              </div>
            </div>
          }
        </div>

        <label class="flex items-start gap-3 rounded-xl border border-slate-700 bg-slate-950/60 p-3 text-sm text-slate-300">
          <input
            type="checkbox"
            [ngModel]="acceptedLegal()"
            (ngModelChange)="acceptedLegal.set(!!$event)"
            name="acceptedLegal"
            class="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500" />
        <span>
            Li e concordo com os <button type="button" (click)="openTerms()" class="underline text-blue-300 hover:text-blue-200">Termos de Serviço</button> e com a <button type="button" (click)="openPrivacy()" class="underline text-blue-300 hover:text-blue-200">Política de Privacidade</button>.
        </span>
      </label>

        <button
          type="submit"
          [disabled]="isRegistering() || !registerForm.form.valid || !acceptedLegal()"
          class="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 font-bold text-white transition hover:opacity-90 disabled:opacity-60">
          @if (isRegistering()) {
            <div class="loader !h-5 !w-5 !border-2 !border-t-white"></div>
            <span class="ml-2">Criando conta...</span>
          } @else {
            <span>Criar conta</span>
          }
        </button>
      </form>

      <p class="mt-6 text-center text-sm text-slate-400">
        Já tem uma conta?
        <button type="button" (click)="goToLogin()" class="font-semibold text-blue-300 hover:text-blue-200">
          Fazer login
        </button>
      </p>
    } @else {
      <div class="text-center">
        <span class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-600/20 text-blue-300">
          <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.24a2.25 2.25 0 0 1-1.07 1.92l-7.5 4.61a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.92v-.24" />
          </svg>
        </span>
        <h2 class="mt-6 text-2xl font-black text-white">Confirme seu e-mail</h2>
        <p class="mt-3 text-sm text-slate-300">
          Enviamos um link para <strong>{{ email }}</strong>. Verifique sua caixa de entrada para ativar sua conta.
        </p>
        <button (click)="goToLogin()" class="mt-8 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 font-bold text-white transition hover:opacity-90">
          Ir para login
        </button>
      </div>
    }
  </div>
</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class RegisterComponent {
  registerSuccess = output<void>();
  requestLogin = output<void>();

  private authService = inject(AuthService);

  isRegistering = signal(false);
  registrationComplete = signal(false);
  name = '';
  email = '';
  password = '';
  acceptedLegal = signal(false);

  readonly passwordStrength = computed(() => {
    const pw = this.password;
    if (pw.length < 6) return { level: 'fraca' as const, percent: 0 };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (pw.length >= 12) score++;
    if (score <= 1) return { level: 'fraca' as const, percent: 33 };
    if (score <= 3) return { level: 'média' as const, percent: 66 };
    return { level: 'forte' as const, percent: 100 };
  });

  async onRegister() {
    this.isRegistering.set(true);
    try {
      await this.authService.signUp(this.email, this.password, this.name, {
        termsAccepted: this.acceptedLegal(),
        privacyAccepted: this.acceptedLegal(),
      });
      this.registrationComplete.set(true);
    } catch (error: any) {
      if (!error?.message?.includes('Consentimento') && !error?.message?.includes('obrigat')) {
        const message = error?.message ?? 'Não foi possível criar a conta. Tente novamente.';
        this.authService.showNotification('error', 'Falha no cadastro', message);
      }
    } finally {
      this.isRegistering.set(false);
    }
  }

  openTerms() {
    window.open('https://www.usecorrectify.com/#termos', '_blank');
  }

  openPrivacy() {
    window.open('https://www.usecorrectify.com/#privacidade', '_blank');
  }

  goToLogin() {
    this.requestLogin.emit();
  }
}
